import type { RangoFechas, ValorPeriodo } from "@/lib/filtros/periodo";
import { crearStoreFiltros, type StoreFiltros } from "@/lib/filtrosPersistentes";
import type { OrdenCampo } from "@/components/admin/portal-tickets/tickets/TicketsTable";
import type { ModoBusquedaTickets } from "@/lib/portal-tickets/busqueda";

/**
 * Filtros de las vistas de tickets, persistidos como en el resto del panel
 * (`filtros:<portal>_<menu>`, ver `crearStoreFiltros`): el logout los barre y
 * cada menú guarda los suyos.
 *
 * Antes se guardaban a mano en `tickets:filtros:<scope>`, y «Todos» y
 * «Pipeline» comparten scope: la vista (tabla/kanban) de uno pisaba la del
 * otro, así que los dos menús terminaban mostrando lo mismo.
 */
export type VistaTickets = "tabla" | "kanban";
/** Pestañas de «Todos»: los antiguos menús Mis tickets y Sin asignar viven aquí desde 2026-10-02. */
/**
 * «creados» = Levantados por mí. Solo existe en los portales que montan tickets con alcance
 * (Cobranza, Productos); el Portal Tickets sigue con sus tres pestañas.
 */
export type PestanaTickets = "todos" | "mios" | "sin-asignar" | "creados";
export const PESTANAS_TICKETS: PestanaTickets[] = ["todos", "mios", "sin-asignar"];
export const PESTANAS_TICKETS_PORTAL: PestanaTickets[] = ["todos", "mios", "creados", "sin-asignar"];
/**
 * Tickets de Portal Cobranza y Portal Productos (20261006205000): cada quien ve solo los suyos,
 * así que no hay «Todos» ni «Sin asignar» de las áreas.
 */
export const PESTANAS_TICKETS_PROPIOS: PestanaTickets[] = ["mios", "creados"];

export interface FiltrosTickets {
  vista: VistaTickets;
  pestana: PestanaTickets;
  /** "todos" = todos los pipelines; "" = aún sin elegir (se fija al primero al cargar). */
  pipelineId: string;
  prioridad: string;
  categoria: string;
  etapas: string[];
  busqueda: string;
  /** Rango de fecha de creación (`YYYY-MM-DD`); vacío = sin rango. */
  fechaDesde: string;
  fechaHasta: string;
  /**
   * "Buscar en" del buscador único (`ui/busqueda-inteligente`): automático o un campo forzado.
   * Incluye «Propietario» y «Levantado por», que antes eran selects propios (ver `migrarFiltrosTickets`).
   */
  buscarEn: ModoBusquedaTickets;
  orden: { campo: OrdenCampo; dir: "asc" | "desc" };
}

const iniciales = (vista: VistaTickets, pestana: PestanaTickets): FiltrosTickets => ({
  vista,
  pestana,
  pipelineId: "",
  prioridad: "todas",
  categoria: "todas",
  etapas: [],
  busqueda: "",
  fechaDesde: "",
  fechaHasta: "",
  buscarEn: "auto",
  orden: { campo: "fechaCreacion", dir: "desc" },
});

// Un store por menú, creado una sola vez: `crearStoreFiltros` devuelve un hook de zustand y no
// puede nacer dentro de un render.
const stores = new Map<string, StoreFiltros<FiltrosTickets>>();

/** Clave de storage de una vista: una por ruta, para que dos menús no compartan filtros. */
export function claveFiltrosTickets(ruta: string): string {
  return `ptickets_${ruta.replace(/^\/admin\//, "").replace(/[^a-z0-9]+/gi, "_")}`;
}

/**
 * v1 (2026-10-02): todas las vistas abren en Etapas (Kanban) y el pipeline inicial se elige
 * por dónde hay tickets del menú. Subir la versión descarta una vez lo guardado, que traía
 * la vista Lista y el primer pipeline del catálogo fijos.
 */
/** v2 (2026-10-27): pestaña inicial «Mis tickets»; descarta una vez el «Todos» guardado. */
const VERSION_FILTROS = 2;

/**
 * Lo guardado antes de 2026-10-19 traía `propietario` y `creador` (ids de usuario de los selects
 * que ahora son modos del buscador). No se traducen a texto: el nombre vive en el catálogo de
 * agentes, que aún no cargó cuando se lee el storage, y un id en el buscador no le dice nada a
 * nadie. Se descartan para no dejar un filtro invisible; el resto de lo guardado se conserva.
 */
export function migrarFiltrosTickets(guardado: Record<string, unknown>): Record<string, unknown> {
  const { propietario: _propietario, creador: _creador, ...resto } = guardado;
  return resto;
}

/** Store de filtros de la vista montada en `ruta`. */
export function filtrosTickets(ruta: string, vistaInicial: VistaTickets = "kanban"): StoreFiltros<FiltrosTickets> {
  const clave = claveFiltrosTickets(ruta);
  let store = stores.get(clave);
  if (!store) {
    // Portal Tickets abre en «Mis tickets» (regla de Eduardo, 2026-10-27): se entra a lo propio y
    // «Todos» se elige a mano. Alta Dirección sigue en «Todos». Una vista sin la pestaña cae en
    // su primera (ver TicketsWorkspace).
    const pestana: PestanaTickets = ruta.startsWith("/admin/portal-tickets") ? "mios" : "todos";
    store = crearStoreFiltros(clave, iniciales(vistaInicial, pestana), {
      version: VERSION_FILTROS,
      migrar: migrarFiltrosTickets,
    });
    stores.set(clave, store);
  }
  return store;
}

/** Filtros y ranking de la pestaña Equipo de Analítica: sobreviven la recarga. */
export type OrdenEquipo = "resueltos" | "vencidos" | "abiertos";
export type VistaGraficaEquipo = "resuelve" | "tiempo" | "debe" | "ritmo";
export type TipoGraficaEquipo = "barras" | "circular" | "puntos";
export const filtrosEquipo = crearStoreFiltros("ptickets_portal_tickets_analitica_equipo", {
  busqueda: "",
  rol: "todos",
  orden: "resueltos" as OrdenEquipo,
  vistaGrafica: "resuelve" as VistaGraficaEquipo,
  tipoGrafica: "barras" as TipoGraficaEquipo,
});

/** Filtros de Analítica (ámbito, periodo y pestaña). La URL `?seccion=` gana al abrir. */
export type AmbitoAnalitica = "cliente" | "interno" | "todos";
/** Mes en curso como periodo (año + mes): el de por omisión de la analítica. */
export const mesEnCurso = (hoy: Date = new Date()): ValorPeriodo => ({ anio: hoy.getFullYear(), mes: hoy.getMonth() + 1 });

// v2 (2026-10-27): el periodo deja de ser "últimos N días" y arranca en el mes en curso (o el
// periodo / rango que se elija, o todo). Regla de Eduardo.
export const filtrosAnalitica = crearStoreFiltros(
  "ptickets_portal_tickets_analitica",
  {
    ambito: "todos" as AmbitoAnalitica,
    periodo: mesEnCurso() as ValorPeriodo,
    rango: null as RangoFechas | null,
    seccion: "equipo",
  },
  { version: 2 },
);
